import { type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { CopyIcon, WalletIcon } from "@components/icons";
import { Input } from "@components/inputs/Input/Input";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { type IUseModalProps } from "@store/modal/modal";
import { type IBankTransferPayment } from "@services/payments/types";
import { copyToClipboard, isNullOrUndefined } from "@utils/common";
import { formatDecimalNumberInputValue } from "@utils/units";

interface IBankTransferInstructionsModalProps extends IUseModalProps {
  payment: IBankTransferPayment;
}

export const BankTransferInstructionsModal: React.FC<
  IBankTransferInstructionsModalProps
> = ({ payment, ...modalProps }) => {
  const modal = useModal(modalProps);
  const { t } = useTranslation();
  const amount = !isNullOrUndefined(payment.amount)
    ? formatDecimalNumberInputValue(payment.amount)
    : "";
  const description = useTranslationArray(
    "bankTransferInstructionsModal.description",
  );

  const selectField = (
    e: MouseEvent<HTMLInputElement, globalThis.MouseEvent>,
    // eslint-disable-next-line
    // @ts-expect-error
    // eslint-disable-next-line
  ) => e?.target?.select?.();

  const copyField = (value?: string | number) =>
    copyToClipboard(
      value,
      t("bankTransferInstructionsModal.toast.copied"),
      t("bankTransferInstructionsModal.toast.copyFailed"),
    );

  return (
    <Modal
      modal={modal}
      actionButtons={
        <div className="flex w-full justify-end">
          <Button
            variant="secondary"
            className="w-full md:w-fit"
            onClick={modal.close}
          >
            {t("bankTransferInstructionsModal.actions.cancel")}
          </Button>
        </div>
      }
      title={t("bankTransferInstructionsModal.title")}
      subtitle={t("bankTransferInstructionsModal.subtitle")}
      badgeProps={{ icon: WalletIcon }}
    >
      <div className="flex flex-col gap-4">
        <Input
          label={t("bankTransferInstructionsModal.fields.receiver.label")}
          value={payment.receiver}
          readOnly={true}
          onClick={selectField}
          iconRight={CopyIcon}
          iconRightTooltipContent={t(
            "bankTransferInstructionsModal.actions.copy",
          )}
          onRightIconClick={() => copyField(payment.receiver)}
        />
        <Input
          label={t("bankTransferInstructionsModal.fields.accountNumber.label")}
          value={payment.bankAccountNumber}
          readOnly={true}
          onClick={selectField}
          iconRight={CopyIcon}
          iconRightTooltipContent={t(
            "bankTransferInstructionsModal.actions.copy",
          )}
          onRightIconClick={() => copyField(payment.bankAccountNumber)}
        />
        <Input
          label={t("bankTransferInstructionsModal.fields.amount.label", {
            currency: payment.currencyId,
          })}
          value={amount}
          readOnly={true}
          onClick={selectField}
          iconRight={CopyIcon}
          iconRightTooltipContent={t(
            "bankTransferInstructionsModal.actions.copy",
          )}
          onRightIconClick={() => copyField(amount)}
        />
        <Input
          label={t("bankTransferInstructionsModal.fields.description.label")}
          value={payment.paymentPurpose}
          readOnly={true}
          onClick={selectField}
          iconRight={CopyIcon}
          iconRightTooltipContent={t(
            "bankTransferInstructionsModal.actions.copy",
          )}
          onRightIconClick={() => copyField(payment.paymentPurpose)}
        />
        <Input
          label={t("bankTransferInstructionsModal.fields.swiftCode.label")}
          value={payment.swiftCode}
          readOnly={true}
          onClick={selectField}
          iconRight={CopyIcon}
          iconRightTooltipContent={t(
            "bankTransferInstructionsModal.actions.copy",
          )}
          onRightIconClick={() => copyField(payment.swiftCode)}
        />
        <div className="flex flex-col gap-3">
          {description?.map((item) => (
            <div className="text-sm text-grey-dark">{item}</div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
